/** Composition root: wires the sim, renderer, UI, input, storage, and bridge. */
import './ui/styles.css';
import { DT, SPEEDS, currentBoard, selectBoard } from './data/balance.js';
import { BOARDS, HISTORICAL_HASHES } from './data/boards.js';
import { computeBalanceHash } from './core/balanceHash.js';
import { Game } from './core/game.js';
import { ReplayPlayer, resumeGame } from './core/replay.js';
import type { GameEvent, Replay } from './core/types.js';
import { Renderer } from './render/renderer.js';
import { createViewState } from './render/viewState.js';
import { byId } from './ui/dom.js';
import { Input } from './ui/input.js';
import { appendLog } from './ui/log.js';
import {
  buildRecipeList,
  hideAllModals,
  hideModal,
  renderLeaderboard,
  showModal,
  showSubmitModal,
  showWonModal,
  wireModals,
  type LeaderboardEntry,
} from './ui/modals.js';
import { Panel } from './ui/panel.js';
import { previewCombine, previewKeep, previewRecipe, previewSlate } from './ui/preview.js';
import { BridgeClient } from './platform/bridge.js';
import { Camera } from './platform/camera.js';
import { Loop } from './platform/loop.js';
import { Sheet } from './ui/sheet.js';
import { clearRun, loadName, loadRun, saveName, saveRun, stashReplay } from './platform/storage.js';

const PARAMS = new URLSearchParams(location.search);
const SESSION = PARAMS.get('session') ?? 'local';
const AUTOSAVE_MS = 3000;

// Board evaluation switch: ?board=current|classic|rift (see data/boards.ts).
// Must run before any Game is constructed -- waypoints feed balanceHash.
const requestedBoard = PARAMS.get('board');
if (requestedBoard && !selectBoard(requestedBoard)) {
  appendLog(`Unknown board "${requestedBoard}" - using ${currentBoard().name}.`, true);
}

function newSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 1e9)) >>> 0;
}

// ---- game state ----

const view = createViewState();
let replayPlayer: ReplayPlayer | null = null;
let game: Game;

const saved = loadRun();
// A save is only resumable under the balance (and board) it was played on:
// resuming re-simulates the input log, so a mismatched balanceHash would
// silently produce a different game. Discard instead.
if (saved && saved.replay.balanceHash !== computeBalanceHash()) {
  clearRun();
  appendLog('Saved run was played on a different board/balance - starting fresh.', true);
  game = new Game({ seed: newSeed(), startedAt: new Date().toISOString() });
} else if (saved) {
  game = resumeGame(saved.replay, saved.tick);
  appendLog(`Resumed your run at wave ${game.wave}.`, true);
} else {
  game = new Game({ seed: newSeed(), startedAt: new Date().toISOString() });
}

const activeGame = (): Game => (replayPlayer ? replayPlayer.game : game);

// ---- rendering + panel ----

const canvas = byId('board', HTMLCanvasElement);
const renderer = new Renderer(canvas);

const panel = new Panel(view, {
  onKeep: (i) => {
    if (!view.replayMode) {
      view.pendingSlate = null;
      game.actionKeep(i);
    }
  },
  onCombine: (i, n) => {
    if (!view.replayMode && game.phase === 'choose' && game.newGems[i]) {
      view.pendingSlate = null;
      game.actionCombine(i, n);
    }
  },
  onRecipe: (i, key) => {
    if (!view.replayMode && game.phase === 'choose' && game.newGems[i]) {
      view.pendingSlate = null;
      game.actionRecipe(i, key);
    }
  },
  onSlateForge: (i, key) => {
    if (view.replayMode || game.phase !== 'choose' || !game.newGems[i]) return;
    // Toggle the socket picker; the forge lands when a rock is tapped.
    view.pendingSlate =
      view.pendingSlate?.i === i && view.pendingSlate.key === key ? null : { i, key };
  },
  onRetire: () => {
    if (!view.replayMode) game.actionRetire();
  },
  onUpgrade: () => {
    if (!view.replayMode) game.upgradeChance();
  },
  onPreviewKeep: (i) => (view.actionPreview = previewKeep(activeGame(), i)),
  onPreviewCombine: (i, n) => (view.actionPreview = previewCombine(activeGame(), i, n)),
  onPreviewRecipe: (i, key) => (view.actionPreview = previewRecipe(activeGame(), i, key)),
  onPreviewSlate: (i, key) => (view.actionPreview = previewSlate(activeGame(), i, key)),
  onClearPreview: () => (view.actionPreview = null),
  onRestart: restart,
});

// ---- events from the sim ----

function processEvents(events: GameEvent[]): void {
  for (const ev of events) {
    if (ev.kind === 'log') appendLog(ev.msg, ev.important);
    else if (ev.kind === 'float') {
      view.floats.push({ x: ev.x, y: ev.y, txt: ev.text, color: ev.color, t: 1 });
    } else if (ev.kind === 'won') {
      if (!view.replayMode && game.recording.player !== 'agent') showWonModal(ev.lives);
    } else if (!view.replayMode) {
      onRunFinished();
    }
  }
}

function onRunFinished(): void {
  const result = game.recording.result;
  if (!result) return;
  clearRun();
  stashReplay(game.recording);
  if (game.recording.player === 'agent') {
    void submitRun('Claude (bot)');
  } else {
    showSubmitModal(
      result.outcome === 'victory'
        ? result.lives > 0
          ? `Victory! Retired at wave ${result.wave} with ${result.lives} lives.`
          : `Victory! Wave 50 conquered — fell at wave ${result.wave} in the endless waves.`
        : `Game over at wave ${result.wave}.`,
      loadName(),
    );
  }
}

async function submitRun(rawName: string): Promise<void> {
  const name = (rawName || 'anonymous').slice(0, 24);
  saveName(name);
  try {
    await fetch(`/api/submit?s=${SESSION}`, {
      method: 'POST',
      body: JSON.stringify({ name, replay: game.recording }),
    });
    appendLog('Run submitted to the leaderboard.', true);
  } catch {
    appendLog('Could not reach leaderboard server.', false);
  }
  hideModal('submitModal');
}

// ---- replays ----

/**
 * A replay only re-simulates on the board it was recorded on. Match by
 * balanceHash; v1 (prototype-era) replays carry no hash and belong to the
 * prototype board. The live board is restored on exit via the page reload.
 */
function boardForReplay(data: Replay): string | null {
  const prev = currentBoard().key;
  for (const b of BOARDS) {
    selectBoard(b.key);
    if (computeBalanceHash() === data.balanceHash) {
      selectBoard(prev);
      return b.key;
    }
  }
  selectBoard(prev);
  if (data.balanceHash === undefined) return 'current';
  // Pre-endless recordings: their hash predates the slates/endless tables but
  // the waves-1-50 balance is identical, so they replay under legacy rules.
  return HISTORICAL_HASHES.get(data.balanceHash) ?? null;
}

function startReplay(data: Replay, name?: string): void {
  hideAllModals();
  const boardKey = boardForReplay(data);
  if (boardKey && boardKey !== currentBoard().key) {
    selectBoard(boardKey);
    appendLog(`Replay was recorded on the ${currentBoard().name} board - switching.`, false);
  }
  replayPlayer = new ReplayPlayer(data, { skipIdle: true });
  view.replayMode = true;
  view.pendingPlace = null;
  view.actionPreview = null;
  view.floats = [];
  setSpeed(2);
  const who = name ?? (data.player === 'agent' ? 'bot' : 'human');
  const kind = data.player === 'agent' ? 'bot' : 'human';
  byId('replayBanner').hidden = false;
  byId('replayLabel').textContent =
    `REPLAY · ${who} (${kind}) · reached wave ${data.result?.wave ?? '?'}`;
  appendLog(`Replaying ${who}'s run.`, true);
}

async function watchReplay(id: string, entry?: LeaderboardEntry): Promise<void> {
  try {
    if (!entry) {
      try {
        const list = (await (await fetch('/api/leaderboard')).json()) as LeaderboardEntry[];
        entry = list.find((e) => e.id === id);
      } catch {
        // leaderboard unreachable: generic label fallback
      }
    }
    const data = (await (await fetch(`/api/replay/${id}`)).json()) as Replay;
    startReplay(data, entry?.name);
  } catch {
    appendLog('Could not load that replay.', false);
  }
}

function exitReplay(): void {
  location.reload(); // a saved live run auto-resumes on boot
}

let runAbandoned = false;

function restart(): void {
  if (!view.replayMode && game.tick > 0 && !game.recording.result) {
    const ok = confirm('Abandon the current run and restart?');
    if (!ok) return;
  }
  // Order matters: the reload fires pagehide, whose autosave hook must not
  // resurrect the run we just cleared.
  runAbandoned = true;
  clearRun();
  location.reload();
}

// ---- speed ----

function setSpeed(idx: number): void {
  loop.speedIdx = idx;
  byId('speedBtn').textContent = `Speed x${SPEEDS[idx] ?? 1}`;
}

// ---- mobile shell: bottom sheet + pannable camera (coarse pointers only) ----

const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
let camera: Camera | null = null;
let sheet: Sheet | null = null;
if (coarsePointer) {
  sheet = new Sheet();
  sheet.activate();
  camera = new Camera(byId('boardWrap'), canvas);
  camera.layout();
  window.addEventListener('resize', () => camera?.layout());
}

// ---- input ----

new Input(canvas, view, {
  game: activeGame,
  onPlace: (x, y) => {
    game.placeStone(x, y);
  },
  onPickup: (x, y) => game.pickupStone(x, y),
  onLift: (x, y) => game.liftPiece(x, y),
  onDrop: (x, y) => game.dropPiece(x, y),
  onKeep: (i) => {
    view.pendingSlate = null;
    game.actionKeep(i);
  },
  onSocket: (x, y) => {
    const pending = view.pendingSlate;
    if (!pending || !game.canSocketAt(pending.key, x, y)) return false;
    game.actionSlate(pending.i, pending.key, x, y);
    view.pendingSlate = null;
    return true;
  },
  onSlateTap: (x, y) => {
    if (game.phase !== 'build') return false;
    const first = view.pendingSlateUp;
    if (!first) {
      view.pendingSlateUp = { x, y };
      return true;
    }
    if (first.x === x && first.y === y) {
      view.pendingSlateUp = null; // tap again to deselect
      return true;
    }
    // Result rises where the first-selected slate stands.
    game.actionSlateUp(first.x, first.y, x, y);
    view.pendingSlateUp = null;
    return true;
  },
  onSpeed: setSpeed,
  onPan: (dx, dy) => camera?.pan(dx, dy),
  onZoom: (mode) => camera?.setMode(mode),
});

// ---- HUD buttons + modals ----

wireModals();
buildRecipeList();
// Horizontal strips scroll with the mouse wheel (their scrollbars are hidden).
for (const id of ['wavePreview', 'chanceBar']) {
  const strip = byId(id);
  strip.addEventListener(
    'wheel',
    (e) => {
      if (e.deltaY !== 0 && strip.scrollWidth > strip.clientWidth) {
        strip.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    },
    { passive: false },
  );
}
byId('speedBtn').addEventListener('click', () => {
  setSpeed((loop.speedIdx + 1) % SPEEDS.length);
});
byId('helpBtn').addEventListener('click', () => {
  showModal('helpModal');
});
byId('recipesBtn').addEventListener('click', () => {
  showModal('recipesModal');
});
byId('restartBtn').addEventListener('click', restart);
byId('exitReplayBtn').addEventListener('click', exitReplay);
byId('ranksBtn').addEventListener('click', () => {
  void (async () => {
    let entries: LeaderboardEntry[] | null;
    try {
      entries = (await (await fetch('/api/leaderboard')).json()) as LeaderboardEntry[];
    } catch {
      entries = null;
    }
    renderLeaderboard(entries, (entry) => {
      void watchReplay(entry.id, entry);
    });
    showModal('leaderboardModal');
  })();
});
byId('submitBtn').addEventListener('click', () => {
  void submitRun(byId('nameInput', HTMLInputElement).value);
});
byId('continueEndlessBtn').addEventListener('click', () => {
  hideModal('wonModal');
});
byId('retireNowBtn').addEventListener('click', () => {
  hideModal('wonModal');
  if (!view.replayMode) game.actionRetire();
});
byId('upChance').addEventListener('click', () => {
  if (!view.replayMode) game.upgradeChance();
});

// ---- autosave (Pillar 5: progress survives interruption) ----

function autosave(): void {
  if (!runAbandoned && !view.replayMode && game.tick > 0 && !game.recording.result) {
    saveRun(game.recording, game.tick);
  }
}
setInterval(autosave, AUTOSAVE_MS);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') autosave();
});
window.addEventListener('pagehide', autosave);

// ---- agent bridge ----

const bridge = new BridgeClient({
  game: () => game,
  replayMode: () => view.replayMode,
  desynced: () => replayPlayer?.desynced ?? false,
  speedIdx: () => loop.speedIdx,
  setSpeed,
  watchReplay: (id) => {
    void watchReplay(id);
  },
  restart: () => {
    runAbandoned = true;
    clearRun();
    location.reload();
  },
  onAgentActive: () => {
    const badge = byId('agentBadge');
    badge.hidden = false;
    clearTimeout(agentBadgeTimer);
    agentBadgeTimer = setTimeout(() => {
      badge.hidden = true;
    }, 2500);
  },
});
let agentBadgeTimer: ReturnType<typeof setTimeout> | undefined;
bridge.start();

// ---- the frame loop ----

let desyncShown = false;

const loop = new Loop(
  () => activeGame().phase !== 'gameover',
  () => {
    if (replayPlayer) replayPlayer.stepTick();
    else game.stepTick();
  },
  (elapsed, ticksRun) => {
    const g = activeGame();
    processEvents(g.drainEvents());
    if (ticksRun > 0) {
      const decay = ticksRun * DT;
      for (const f of view.floats) f.t -= decay;
      view.floats = view.floats.filter((f) => f.t > 0);
    }
    if (replayPlayer?.desynced && !desyncShown) {
      desyncShown = true;
      byId('replayLabel').textContent += ' · ⚠ DESYNC (recorded on different game version)';
    }
    view.animT += elapsed;
    sheet?.onPhase(g.phase);
    panel.render(g);
    panel.tick(g);
    renderer.draw(g, view);
  },
);

appendLog(`Board: ${currentBoard().name} (?board=current|classic|rift to compare).`, false);
appendLog(`Session ${SESSION} ready.`, false);
setSpeed(0);
loop.start();
